/* eslint-disable jsx-a11y/img-redundant-alt */
import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { isElectronApp, isEmpty } from '../../helpers/common';
import { DropBox, ScreenRecordMain, YoutubePlayer } from './styles';
import copy from 'clipboard-copy';
import { captureException } from '../../services/logService';
import ReactPlayer from 'react-player';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { Dropdown, DropdownItem } from '../dropdown';
import { dispatchTaskSuccess } from '../../services/taskServices';
import { observer } from 'mobx-react-lite';
import IconButton from '../button/iconButton';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../global/constants';
import Image from '../image';

interface Props {
  videoId?: string;
  videoName: string;
  videoUrl: string;
  isYouTubePreview?: boolean;
  isStartFromVideo?: boolean;
}

const RenderVideoFile: React.FC<Props> = (props) => {
  const { videoId, videoName, videoUrl, isYouTubePreview = false, isStartFromVideo = false } = props;
  //Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerWrapperRef = useRef<HTMLDivElement>(null);
  //States
  const [isPlaying, setIsPlaying] = useState(false);
  const [oncePlay, setOncePlay] = useState(false);

  const togglePlay = () => {
    if (!isYouTubePreview) {
      if (videoRef.current?.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current?.pause();
        setIsPlaying(false);
      }
    } else {
      setIsPlaying(!isPlaying);
      setOncePlay(true);
    }
  };
  const handleStandardFullscreen = useCallback(
    (
      ref: RefObject<
        HTMLElement & {
          mozRequestFullScreen?: () => void;
          webkitRequestFullscreen?: () => void;
          msRequestFullscreen?: () => void;
        }
      >
    ) => {
      if (!ref.current) return;
      if (ref.current?.requestFullscreen) {
        if (isElectronApp()) {
          window.open(videoUrl, '_blank');
        } else {
          ref.current.requestFullscreen();
        }
      } else if (ref.current?.mozRequestFullScreen) {
        ref.current.mozRequestFullScreen();
      } else if (ref.current?.webkitRequestFullscreen) {
        ref.current.webkitRequestFullscreen();
      } else if (ref.current?.msRequestFullscreen) {
        ref.current.msRequestFullscreen();
      }
    },
    [videoUrl]
  );

  const openInNewWindow = () => {
    // Pause the video if it's currently playing
    if (!isYouTubePreview && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else if (isYouTubePreview) {
      setIsPlaying(false);
    }
    if (isYouTubePreview && videoId) {
      // For YouTube videos, open the YouTube link in a new tab
      window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
    } else if (videoId) {
      // For screen recordings, open the share URL in a new tab
      window.open(`https://share.teamcamp.app/${videoId}`, '_blank');
    }
  };

  const toggleFullscreen = () => {
    if (!isYouTubePreview) {
      handleStandardFullscreen(videoRef);
      videoRef.current?.play(); // Ensure the video plays when entering fullscreen
      setIsPlaying(true); // Set playing state
    } else {
      if (!isPlaying) {
        // If the video is in preview mode (light is enabled), start playing
        setIsPlaying(true); // Play the video
        setOncePlay(true);
      }
      handleStandardFullscreen(playerWrapperRef);
    }
  };

  const onClickShare = useCallback(
    async (share_id: string | undefined) => {
      try {
        if (!isEmpty(share_id)) {
          if (isYouTubePreview) {
            await copy(`https://www.youtube.com/watch?v=${share_id}`);
            dispatchTaskSuccess('YouTube url copied successfully!');
          } else {
            await copy(`https://share.teamcamp.app/${share_id}`);
            dispatchTaskSuccess('Screen recording url copied successfully!');
          }
        } else {
          await copy(videoUrl);
          dispatchTaskSuccess('Video url copied successfully!');
        }
      } catch (error) {
        captureException(error);
      }
    },
    [isYouTubePreview, videoUrl]
  );

  const downloadVideo = useCallback(async () => {
    try {
      if (videoUrl) {
        // Show loading message
        dispatchTaskSuccess('Starting video download...');

        // Fetch the video data
        const response = await fetch(videoUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the video blob
        const videoBlob = await response.blob();

        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(videoBlob);

        // Create download link
        const link = document.createElement('a');
        link.href = blobUrl;

        // Extract filename from URL or use videoName
        const urlParts = videoUrl?.split('/');
        const fileName = videoName || urlParts[urlParts.length - 1]?.split('?')[0] || 'video.mp4';

        link.download = fileName;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL
        window.URL.revokeObjectURL(blobUrl);

        dispatchTaskSuccess('Video download completed successfully!');
      }
    } catch (error) {
      captureException(error);
      dispatchTaskSuccess('Failed to download video. Please try again.');
    }
  }, [videoUrl, videoName]);

  const renderMoreMenuContent = useMemo(
    () => (
      <IconButton
        iconName='new-three-dots-vertical-icon'
        size={ICON_BUTTON_SIZE.EXTRA_SMALL}
        variant={ICON_BUTTON_TYPE.TERTIARY}
        id='more-video-refresh-ref'
      />
    ),
    []
  );

  return (
    <ScreenRecordMain isStartFromVideo={isStartFromVideo}>
      <div className='video-preview'>
        <div className='screen-recording-header'>
          <div className='screen-recording-desc-div'>
            {isYouTubePreview && (
              <Image imageKey='youtube_icon' alt='youtube' width='20' height='20' className='youtube-icon' />
            )}
            <div className='screen-recording-text'>
              <div className='screen-recording-name'>{isYouTubePreview ? `YouTube_video_${videoName}` : videoName}</div>
            </div>
          </div>
          <div className='screen-recording-right-div'>
            <IconButton
              iconName='link-open-icon'
              size={ICON_BUTTON_SIZE.EXTRA_SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={openInNewWindow}
              id='screen-recording-open'
            />

            <Dropdown modalTitle='Share' activeClassName='active' content={renderMoreMenuContent}>
              <div>
                <DropBox>
                  {!isYouTubePreview && (
                    <DropdownItem
                      label='Download'
                      onClick={downloadVideo}
                      iconName='download-icon'
                      iconSize={16}
                      iconViewBox='0 0 18 18'
                      iconTone='fill'
                    />
                  )}
                  <DropdownItem
                    label='Copy link'
                    onClick={() => onClickShare(videoId)}
                    iconName='copy-link-icon'
                    iconSize={16}
                    iconViewBox='0 0 18 18'
                    iconTone='fill'
                  />
                </DropBox>
              </div>
            </Dropdown>
          </div>
        </div>
        <div className='inbox-image' onDoubleClick={toggleFullscreen}>
          {isYouTubePreview ? (
            <YoutubePlayer ref={playerWrapperRef}>
              <ReactPlayer
                url={videoUrl}
                playing={isPlaying} // Video will play when isPlaying is true
                controls={true} // Show YouTube controls for forward/backward seeking
                height='100%'
                width='100%'
                light={
                  !isPlaying &&
                  !oncePlay && (
                    <Image
                      src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                      alt='Thumbnail'
                      width={353}
                      height={200}
                      className='img-thumbnail'
                    />
                  )
                } // Only show the thumbnail initially
                config={{
                  youtube: {
                    playerVars: {
                      modestbranding: 1, // Minimal branding
                      showinfo: 0, // Hide video info
                      controls: 1, // Show YouTube controls
                      disablekb: 0, // Enable keyboard controls for seeking
                      iv_load_policy: 3, // Hide annotations
                      fs: 1, // Allow full-screen
                      rel: 0 // Don't show related videos
                    }
                  }
                }}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
              />
            </YoutubePlayer>
          ) : (
            <video ref={videoRef} controls={false} className='inbox-video'>
              <source type='video/mp4' src={videoUrl} />
              <source type='video/webm' src={videoUrl.replace(/\.mp4$/i, '.webm')} />
            </video>
          )}
          <div role='button' onClick={togglePlay}>
            {isPlaying ? (
              <SVGIcon name='video-pause-icon' width='30' height='30' viewBox='0 0 30 30' className='video-icon' />
            ) : (
              <Image imageKey='video_play' alt='video-play' className='video-icon' width='30' height='30' />
            )}
          </div>
        </div>
      </div>
    </ScreenRecordMain>
  );
};

export default observer(RenderVideoFile);
